import * as React from "react";
import { observer } from "mobx-react";
import axios from "axios";
import call from "../../call";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Title from "./Title";
import Pagination from "@mui/material/Pagination";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import { UserDataStoreContext } from "../../store";
import TableSortLabel from "@mui/material/TableSortLabel";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";

const Clients = observer(() => {
  const [clients, setClients] = React.useState({ data: [], meta: { total: 0, page: 1, limit: 10 } });
  const [page, setPage] = React.useState(1);
  const [sortBy, setSortBy] = React.useState("createdAt");
  const [order, setOrder] = React.useState("DESC");
  const [nameFilter, setNameFilter] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [createdFrom, setCreatedFrom] = React.useState("");
  const [createdTo, setCreatedTo] = React.useState("");
  const navigate = useNavigate();
  const userDataStore = React.useContext(UserDataStoreContext);
  const isAdmin = userDataStore.roles.includes("ROLE_ADMIN");

  const update = (newPage, params = {}) => {
    call(
      axios({
        method: "get",
        url: "/api/demandbase/client",
        params: {
          page: newPage,
          limit: 10,
          ...params,
        },
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }),
    ).then((resp) => {
      setClients({
        data: resp.data.data,
        meta: resp.data.meta
      });
    });
  };

  React.useEffect(() => {
    const params = {
      sort_by: sortBy,
      order: order,
      name: nameFilter,
      status: statusFilter,
      created_from: createdFrom,
      created_to: createdTo,
    };

    const timeout = setTimeout(() => {
      update(page, params);
    }, 400);

    return () => clearTimeout(timeout);
  }, [page, sortBy, order, nameFilter, statusFilter, createdFrom, createdTo]);

  const handleRequestSort = (field) => {
    if (sortBy === field) {
      setOrder(order === "ASC" ? "DESC" : "ASC");
    } else {
      setSortBy(field);
      setOrder(field === "createdAt" ? "DESC" : "ASC");
    }
    setPage(1);
  };

  const getStatusLabel = (client) => {
    if (client.isDisabled) {
      return "Disabled";
    }
    return "Active";
  };

  const tableCellStyle = {
    paddingTop: "13px",
  };

  return (
    <React.Fragment>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Title>Clients</Title>
        {isAdmin && (
          <Button
            variant="contained"
            onClick={() => navigate("/integrations/demandbase/clients/new")}
          >
            Add new client
          </Button>
        )}
      </Box>

      <Stack direction="row" spacing={2} sx={{ mb: 2, mt: 2 }}>
        <TextField
          label="Client name"
          value={nameFilter}
          onChange={(e) => {
            setNameFilter(e.target.value);
            setPage(1);
          }}
          size="small"
        />

        <TextField
          select
          label="Status"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          size="small"
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="disabled">Disabled</MenuItem>
        </TextField>

        <TextField
          label="Created from"
          type="date"
          value={createdFrom}
          onChange={(e) => {
            setCreatedFrom(e.target.value);
            setPage(1);
          }}
          size="small"
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Created to"
          type="date"
          value={createdTo}
          onChange={(e) => {
            setCreatedTo(e.target.value);
            setPage(1);
          }}
          size="small"
          InputLabelProps={{ shrink: true }}
        />

        <Button
          variant="outlined"
          onClick={() => {
            setNameFilter("");
            setStatusFilter("all");
            setCreatedFrom("");
            setCreatedTo("");
            setSortBy("createdAt");
            setOrder("DESC");
            setPage(1);
          }}
        >
          Reset
        </Button>
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sortDirection={sortBy === "name" ? order.toLowerCase() : false}>
              <TableSortLabel
                active={sortBy === "name"}
                direction={sortBy === "name" ? order.toLowerCase() : "asc"}
                onClick={() => handleRequestSort("name")}
              >
                Name
              </TableSortLabel>
            </TableCell>
            <TableCell>Token</TableCell>
            <TableCell sortDirection={sortBy === "status" ? order.toLowerCase() : false}>
              <TableSortLabel
                active={sortBy === "status"}
                direction={sortBy === "status" ? order.toLowerCase() : "asc"}
                onClick={() => handleRequestSort("status")}
              >
                Status
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortBy === "createdAt" ? order.toLowerCase() : false}>
              <TableSortLabel
                active={sortBy === "createdAt"}
                direction={sortBy === "createdAt" ? order.toLowerCase() : "desc"}
                onClick={() => handleRequestSort("createdAt")}
              >
                Created At
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortBy === "lastListReceivedAt" ? order.toLowerCase() : false}>
              <TableSortLabel
                active={sortBy === "lastListReceivedAt"}
                direction={sortBy === "lastListReceivedAt" ? order.toLowerCase() : "desc"}
                onClick={() => handleRequestSort("lastListReceivedAt")}
              >
                Last List
              </TableSortLabel>
            </TableCell>
            {isAdmin && <TableCell></TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {clients.data.map((client) => (
            <TableRow key={client.id} sx={{ verticalAlign: "top" }}>
              <TableCell sx={tableCellStyle}>{client.name}</TableCell>
              <TableCell sx={tableCellStyle}>
                {client.apiTokenMasked}
              </TableCell>
              <TableCell sx={tableCellStyle}>
                {getStatusLabel(client)}
              </TableCell>
              <TableCell sx={tableCellStyle}>
                {client.createdAt ? new Date(client.createdAt).toLocaleString() : ""}
              </TableCell>
              <TableCell sx={tableCellStyle}>
                {client.lastListReceivedAt
                  ? new Date(client.lastListReceivedAt).toLocaleString()
                  : "—"}
              </TableCell>
              {isAdmin && (
                <TableCell sx={tableCellStyle} align="right">
                  <IconButton
                    aria-label="edit"
                    onClick={() =>
                      navigate(
                        `/integrations/demandbase/clients/${client.id}/edit`,
                      )
                    }
                    size="small"
                  >
                    <EditIcon fontSize="inherit" />
                  </IconButton>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
        <Pagination
          count={clients.meta?.totalPages || 1}
          page={page}
          onChange={(event, newPage) => setPage(newPage)}
          color="primary"
        />
      </Box>
    </React.Fragment>
  );
});

export default Clients;

